import dayjs from "dayjs";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import {
  BETWEEN,
  DATE,
  GTE,
  LIKE,
  LTE,
  STRING,
} from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import DatePicker from "../../framework/date/DatePicker";
import GlobalContext from "../../framework/GlobalContext";
import NumberInput from "../../framework/NumberInput";
import { executeScroll, isEmpty } from "../../utils/utils";
import Shortcut from "../../framework/Shortcut";
import SelectInput from "../../framework/SelectInput";
import {
  fullfillmentStatusOptions,
  statusOptions,
  transferReasons,
} from "../../utils/constant";
import { fetchBranches } from "../../services/branch-service";
const StockTransferReportFilter = ({
  onFilterShow,
  applyFilterOnMount = true,
  reportRef,
}) => {
  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [branchSuggestions, setBranchSuggestions] = useState([]);
  const [selectedFromBranch, setSelectedFromBranch] = useState([]);
  const [selectedToBranch, setSelectedToBranch] = useState([]);
  const [limit, setLimit] = useState(100);
  const [status, setStatus] = useState("");
  const [fullfillmentStatus, setFullfillmentStatus] = useState("");
  const [reason, setReason] = useState("");
  const filterStatusRef = useRef();
  const filterFullFillmentStatusRef = useRef();
  const filterReasonRef = useRef();
  const filterStartDateRef = useRef();
  const filterEndDateRef = useRef();
  const filterFromBranchIdRef = useRef();
  const filterToBranchIdRef = useRef();
  const filterLimitRef = useRef();
  const [orderBy] = useState([
    { fieldName: "businessDate", orderType: "desc" },
  ]);

  const { setCurrentComponent } = useContext(GlobalContext);

  const handleFetchBranches = useCallback((event) => {
    setBranchSuggestions([]);
    fetchBranches(`deleted=false`).then((resp) => {
      let updatedResp = resp.result.rows.map((branch) => ({
        id: branch._id,
        label: branch.code,
      }));
      setBranchSuggestions(updatedResp);
    });
  }, []);

  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      const _startDate = startDate?.format("DD/MM/YYYY");
      const _endDate = endDate?.format("DD/MM/YYYY");
      if (_startDate && !_endDate) {
        filters.push(createFilterParam("businessDate", _startDate, DATE, GTE));
      }
      if (!_startDate && _endDate) {
        filters.push(createFilterParam("businessDate", _endDate, DATE, LTE));
      }
      if (_startDate && _endDate) {
        filters.push(
          createFilterParam(
            "businessDate",
            { start: _startDate, end: _endDate },
            DATE,
            BETWEEN
          )
        );
      }
      if (!isEmpty(selectedFromBranch)) {
        filters.push(
          createFilterParam(
            "fromBranchId",
            selectedFromBranch?.label,
            STRING,
            LIKE
          )
        );
      }
      if (!isEmpty(selectedToBranch)) {
        filters.push(
          createFilterParam("branchId", selectedToBranch?.label, STRING, LIKE)
        );
      }
      if (!isEmpty(status)) {
        filters.push(createFilterParam("status", status, "string", LIKE));
      }
      if (!isEmpty(fullfillmentStatus)) {
        filters.push(
          createFilterParam(
            "fullfillmentStatus",
            fullfillmentStatus,
            "string",
            LIKE
          )
        );
      }
      if (!isEmpty(reason)) {
        filters.push(createFilterParam("reason", reason, "string", LIKE));
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      if (orderBy) {
        result.orderBy = orderBy;
      }
      if (!isEmpty(limit)) {
        result.limit = limit;
      }

      reportRef.current?.applyFilters(result);
    },
    [
      selectedFromBranch,
      selectedToBranch,
      endDate,
      fullfillmentStatus,
      limit,
      orderBy,
      reason,
      reportRef,
      startDate,
      status,
    ]
  );

  const filterListener = useCallback(
    (event) => {
      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey;
      if (ctrl && key === "enter") {
        applyFilterHandler(true);
        return;
      }
    },
    [applyFilterHandler]
  );
  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="str_filter_start_date"
            label="Start Date"
            inputRef={filterStartDateRef}
            onChange={(value) => setStartDate(value)}
            onFocus={() => setCurrentComponent("str_filter_start_date")}
            value={startDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "str_filter_start_date",
                down: "str_filter_end_date",
                left: "str_filter_start_date",
                right: "str_filter_end_date",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="str_filter_end_date"
            label="End Date"
            inputRef={filterEndDateRef}
            onChange={(value) => setEndDate(value)}
            onFocus={() => {
              executeScroll(filterEndDateRef);
              setCurrentComponent("str_filter_end_date");
            }}
            value={endDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "str_filter_start_date",
                down: "str_filter_from_branch",
                left: "str_filter_start_date",
                right: "str_filter_from_branch",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="str_filter_from_branch"
            label="From Branch"
            value={selectedFromBranch}
            ref={filterFromBranchIdRef}
            options={branchSuggestions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => {
              return option.label === value.label;
            }}
            onChange={(e, value) => {
              setSelectedFromBranch(value);
            }}
            onFocus={() => {
              executeScroll(filterFromBranchIdRef);
              setCurrentComponent("str_filter_from_branch");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "str_filter_end_date",
              down: "str_filter_to_branch",
              left: "str_filter_end_date",
              right: "str_filter_to_branch",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="str_filter_to_branch"
            label="To Branch"
            value={selectedToBranch}
            ref={filterToBranchIdRef}
            options={branchSuggestions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => {
              return option.label === value.label;
            }}
            onChange={(e, value) => {
              setSelectedToBranch(value);
            }}
            onFocus={() => {
              executeScroll(filterToBranchIdRef);
              setCurrentComponent("str_filter_to_branch");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "str_filter_from_branch",
              down: "str_filter_status",
              left: "str_filter_from_branch",
              right: "str_filter_status",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="str_filter_status"
            label="Status"
            ref={filterStatusRef}
            options={statusOptions.map((status) => ({
              value: status,
              label: status,
            }))}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            value={status}
            onChange={(e, option) => setStatus(option?.value || "")}
            onFocus={() => {
              executeScroll(filterStatusRef);
              setCurrentComponent("str_filter_status");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "str_filter_to_branch",
              down: "str_filter_fullfillment_status",
              left: "str_filter_to_branch",
              right: "str_filter_fullfillment_status",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="str_filter_fullfillment_status"
            label="Fullfillment Status"
            ref={filterFullFillmentStatusRef}
            options={fullfillmentStatusOptions.map((fullfillmentStatus) => ({
              value: fullfillmentStatus,
              label: fullfillmentStatus,
            }))}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            value={fullfillmentStatus}
            onChange={(e, option) => setFullfillmentStatus(option?.value || "")}
            onFocus={() => {
              executeScroll(filterFullFillmentStatusRef);
              setCurrentComponent("str_filter_fullfillment_status");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "str_filter_status",
              down: "str_filter_reason",
              left: "str_filter_status",
              right: "str_filter_reason",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="str_filter_reason"
            label="Transfer Reason"
            ref={filterReasonRef}
            options={transferReasons.map((reason) => ({
              value: reason,
              label: reason,
            }))}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            value={reason}
            onChange={(e, option) => setReason(option?.value || "")}
            onFocus={() => {
              executeScroll(filterReasonRef);
              setCurrentComponent("str_filter_reason");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "str_filter_fullfillment_status",
              down: "str_filter_limit",
              left: "str_filter_fullfillment_status",
              right: "str_filter_limit",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="str_filter_limit"
            label="Limit"
            ref={filterLimitRef}
            onValueChange={(values) => setLimit(values.floatValue)}
            isAllowed={(values) => values?.formattedValue <= 999999}
            onFocus={() => {
              executeScroll(filterLimitRef);
              setCurrentComponent("str_filter_limit");
            }}
            value={limit}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "str_filter_reason",
                down: "str_apply_filter_button",
                left: "str_filter_reason",
                right: "str_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="str_apply_filter_button"
            label="Apply"
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => applyFilterHandler(true)}
            markers={JSON.stringify({
              up: "str_filter_limit",
              left: "str_filter_limit",
              down: "str_apply_filter_button",
              right: "str_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  useEffect(() => {
    handleFetchBranches();
  }, [handleFetchBranches]);
  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterOnMount]);

  useEffect(() => {
    if (onFilterShow) {
      filterStartDateRef?.current?.focus({ preventScroll: true });
    }
  }, [onFilterShow]);

  useEffect(() => {
    if (onFilterShow) {
      document.addEventListener("keyup", filterListener);
    }
    return () => {
      document.removeEventListener("keyup", filterListener);
    };
  }, [filterListener, onFilterShow]);

  return (
    <Filters
      title="Stock Transfer Report Filters"
      filterContent={filterContent}
      filterFooter={filterFooter}
    />
  );
};

export default StockTransferReportFilter;
